import { Component, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import {
  VisitCommentService,
  VisitComment,
  CreateVisitCommentRequest,
} from '@beautyapp/core';
import { VisitAddFacade } from '@beautyapp/shared';

@Component({
  selector: 'beautyapp-visit-comments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzSpinModule,
    NzEmptyModule,
    NzButtonModule,
    NzIconModule,
    NzTagModule,
    NzInputModule,
    NzSelectModule,
    NzPopconfirmModule,
    NzAvatarModule,
    NzToolTipModule,
    TranslateModule,
    DatePipe,
  ],
  templateUrl: './visit-comments.component.html',
  styleUrls: ['./visit-comments.component.scss'],
})
export class VisitCommentsComponent implements OnInit {
  visitId = input.required<number>();
  reloadCount = input<number>();
  commentsChanged = output<void>();

  private visitCommentService = inject(VisitCommentService);
  visitAddFacade = inject(VisitAddFacade);

  comments = signal<VisitComment[]>([]);
  loading = signal(false);
  submitting = signal(false);

  // New comment form
  newCommentText = '';
  selectedBookingId: string | null = null;
  selectedBookingServiceId: string | null = null;

  // Edit state
  editingCommentId: string | null = null;
  editingText = '';

  // Available booking services for the selected booking
  availableBookingServices = signal<any[]>([]);

  constructor() {
    effect(() => {
      const rc = this.reloadCount();
      if (rc) {
        this.loadComments();
      }
    });
  }

  ngOnInit(): void {
    this.loadComments();
  }

  loadComments(): void {
    const id = this.visitId();
    if (!id) return;

    this.loading.set(true);
    this.visitCommentService.getComments(id).subscribe({
      next: (res) => {
        this.comments.set(res || []);
        this.loading.set(false);
      },
      error: () => {
        this.comments.set([]);
        this.loading.set(false);
      },
    });
  }

  private reloadAfterChange(): void {
    this.loadComments();
    this.commentsChanged.emit();
  }

  onBookingChange(bookingId: string | null): void {
    this.selectedBookingId = bookingId;
    this.selectedBookingServiceId = null;

    if (bookingId) {
      const booking = this.visitAddFacade.bookings().find((b: any) => b.id === bookingId);
      this.availableBookingServices.set(booking?.bookingServices || []);
    } else {
      this.availableBookingServices.set([]);
    }
  }

  submitComment(): void {
    if (!this.newCommentText.trim()) return;

    const id = this.visitId();
    if (!id) return;

    this.submitting.set(true);

    const dto: CreateVisitCommentRequest = {
      text: this.newCommentText.trim(),
    };
    if (this.selectedBookingId) {
      dto.bookingId = this.selectedBookingId;
    }
    if (this.selectedBookingServiceId) {
      dto.bookingServiceId = this.selectedBookingServiceId;
    }

    this.visitCommentService.createComment(id, dto).subscribe({
      next: () => {
        this.newCommentText = '';
        this.selectedBookingId = null;
        this.selectedBookingServiceId = null;
        this.availableBookingServices.set([]);
        this.submitting.set(false);
        this.reloadAfterChange();
      },
      error: () => {
        this.submitting.set(false);
      },
    });
  }

  startEdit(comment: VisitComment): void {
    this.editingCommentId = comment.id;
    this.editingText = comment.text;
  }

  cancelEdit(): void {
    this.editingCommentId = null;
    this.editingText = '';
  }

  saveEdit(comment: VisitComment): void {
    if (!this.editingText.trim()) return;

    this.visitCommentService
      .updateComment(this.visitId(), comment.id, { text: this.editingText.trim() })
      .subscribe({
        next: () => {
          this.editingCommentId = null;
          this.editingText = '';
          this.reloadAfterChange();
        },
      });
  }

  deleteComment(comment: VisitComment): void {
    this.visitCommentService.deleteComment(this.visitId(), comment.id).subscribe({
      next: () => {
        this.reloadAfterChange();
      },
    });
  }

  pinComment(comment: VisitComment): void {
    this.visitCommentService.pinComment(this.visitId(), comment.id).subscribe({
      next: () => {
        this.reloadAfterChange();
      },
    });
  }

  unpinComment(comment: VisitComment): void {
    this.visitCommentService.unpinComment(this.visitId(), comment.id).subscribe({
      next: () => {
        this.reloadAfterChange();
      },
    });
  }

  getInitials(name?: string): string {
    if (!name || !name.trim()) return '?';
    return name
      .trim()
      .split(/\s+/)
      .filter((n) => n.length > 0)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '?';
  }
}
